import { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import { Chart, getDatasetAtEvent } from 'react-chartjs-2';
import { black, white } from '../utils/variables';
import { getGlobalName, getImpactName } from '../utils/utils';

export default function ImpactGraph(props: {
  impact_lifecycle: any;
  impact_transport: any;
  impacts_global: any;
}) {
  const [impact, setImpact] = useState<string>('ecs');
  const [global, setGlobal] = useState<any>({});
  const [matPrem, setMatPrem] = useState<any>({});
  const [transfo, setTransfo] = useState<any>({});
  const [transport, setTransport] = useState<any>({});
  const [util, setUtil] = useState<any>({});
  const [finDeVie, setFinDeVie] = useState<any>({});
  const [active, setActive] = useState<number>(-1);

  function sortObject(object: any) {
    return Object.entries(object)
      .sort((a: any, b: any) => b[1] - a[1])
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
  }

  const units: any = {
    ecs: 'Pts',
    acd: 'mol H+ eq',
    ozd: 'kg CFC-11 eq',
    cch: 'kg CO2 eq',
    fwe: 'kg P eq',
    swe: 'kg N eq',
    tre: 'mol N eq',
    pco: 'kg NMVOC eq',
    pma: 'disease incidences',
    ior: 'kg Bq-U235 eq',
    fru: 'MJ',
    mru: 'kg Sb eq',
    ldu: 'Pt',
    bvi: 'pt',
    etf: 'CTUe',
    htc: 'CTUh',
    htn: 'CTUh',
    //pef: 'PT PEF',
    wtu: 'm³',
  };

  function calculateMax() {
    return Math.max(
        (props.impact_lifecycle.Matierespremieres.global[impact] || 0) +
        (props.impact_lifecycle.Filature.global[impact] || 0) +
        (props.impact_lifecycle['Tissage&Tricotage'].global[impact] || 0) +
        (props.impact_lifecycle.Ennoblissement.global[impact] || 0) +
        (props.impact_lifecycle.Confection.global[impact] || 0) +
        (props.impact_transport[impact] || 0) +
        (props.impact_lifecycle.Utilisation.global[impact] || 0) +
        (props.impact_lifecycle.Findevie.global[impact] || 0),
      0.1, // Valeur minimale pour éviter un graphique vide
    );
  }

  const lifecycle = [
    'Matières premières',
    'Transformation',
    'Transports',
    'Utilisation',
    'Fin de vie',
  ];

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
  );

  const matPremiereData: number =
    parseFloat(props.impact_lifecycle.Matierespremieres.global[impact]) || 0;

  const transformationData: number =
    (parseFloat(props.impact_lifecycle.Filature.global[impact]) || 0) +
    (parseFloat(props.impact_lifecycle['Tissage&Tricotage'].global[impact]) ||
      0) +
    (parseFloat(props.impact_lifecycle.Ennoblissement.global[impact]) || 0) +
    (parseFloat(props.impact_lifecycle.Confection.global[impact]) || 0);

  const transportData: number = parseFloat(props.impact_transport[impact]) || 0;
  const utilisationData: number =
    parseFloat(props.impact_lifecycle.Utilisation.global[impact]) || 0;
  const finDeVieData: number =
    parseFloat(props.impact_lifecycle.Findevie.global[impact]) || 0;


  const globalData: number =
    matPremiereData +
    transformationData +
    transportData +
    utilisationData +
    finDeVieData;


  const colors = ['#69AFCC', '#F4C6A3', '#4B7182', '#B4D7E6', '#E98D46'];

  //TODO ajuster la couleur du graph (en test)
  const adjustColorBrightness = (color: string, amount: number) => {
    let usePound = false;
    if (color[0] === '#') {
      color = color.slice(1);
      usePound = true;
    }

    const num = parseInt(color, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;

    const newColor = (
      0x1000000 +
      (r < 255 ? (r < 1 ? 0 : r) : 255) * 0x10000 +
      (g < 255 ? (g < 1 ? 0 : g) : 255) * 0x100 +
      (b < 255 ? (b < 1 ? 0 : b) : 255)
    )
      .toString(16)
      .slice(1);

    return (usePound ? '#' : '') + newColor;
  };

  const getBarStyles = (index: number) => {
    const baseColor = colors[index];
    if (active === -1) {
      return { backgroundColor: baseColor };
    } else {
      return {
        backgroundColor:
          active === index
            ? adjustColorBrightness(baseColor, -30)
            : adjustColorBrightness(baseColor, 30),
      };
    }
  };

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        labels: {
          color: window.matchMedia('(prefers-color-scheme: dark)').matches
            ? white
            : black,
          font: {
            size: 15,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = '';
            if (context.parsed.y !== null) {
              label += context.parsed.x;
            }
            label += ' ' + units[impact];
            return label;
          },
          title: function () {
            return '';
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: false,
        beginAtZero: true,
        max: calculateMax(),
        min: 0,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
    animation: {
      onComplete: () => {
        if (chartRef.current) {
          chartRef.current.update();
        }
      },
    },
  };

  const data: ChartData<'bar'> = {
    labels: ['Impacts'],
    datasets: [
      {
        label: lifecycle[0],
        data: [matPremiereData || 0],
        ...getBarStyles(0),
        barThickness: 8,
        borderRadius: 30,
      },
      {
        label: lifecycle[1],
        data: [transformationData || 0],
        ...getBarStyles(1),
        barThickness: 8,
        borderRadius: 30,
      },
      {
        label: lifecycle[2],
        data: [transportData || 0],
        ...getBarStyles(2),
        barThickness: 8,
        borderRadius: 30,
      },
      {
        label: lifecycle[3],
        data: [utilisationData || 0],
        ...getBarStyles(3),
        barThickness: 8,
        borderRadius: 30,
      },
      {
        label: lifecycle[4],
        data: [finDeVieData || 0],
        ...getBarStyles(4),
        barThickness: 8,
        borderRadius: 30,
      },
    ],
  };

  function handleClick(e: any) {
    const dataset = getDatasetAtEvent(chartRef.current!!, e);
    if (dataset.length > 0) {
      const clickedIndex = dataset[0].datasetIndex;
      if (active === clickedIndex) {
        setActive(-1);
      } else {
        setActive(clickedIndex);
      }
    }
  }

  const handleImpactClick = (i: string) => {
    if (impact === i) {
      setImpact('ecs');
    } else {
      setImpact(i);
    }
  };

  const handleLineClick = (dataset: any, index: number) => {
    setActive(index);
    setImpact('ecs');
  };

  const handleReset = () => {
    setActive(-1);
    setImpact('ecs');
  };

  const totalPercentageMatPremiere = Object.keys(matPrem)
    .map((i: string) => (parseFloat(matPrem[i]) ? parseFloat(matPrem[i]) : 0))
    .reduce((acc, curr) => acc + curr, 0);

  const totalPercentageTransformation = Object.keys(transfo)
    .map((i: string) => (parseFloat(transfo[i]) ? parseFloat(transfo[i]) : 0))
    .reduce((acc, curr) => acc + curr, 0);

  const totalPercentageTransport = Object.keys(transport)
    .map((i: string) =>
      parseFloat(transport[i]) ? parseFloat(transport[i]) : 0,
    )
    .reduce((acc, curr) => acc + curr, 0);

  const totalPercentageUtilisation = Object.keys(util)
    .map((i: string) => (parseFloat(util[i]) ? parseFloat(util[i]) : 0))
    .reduce((acc, curr) => acc + curr, 0);

  const totalPercentageFinDeVie = Object.keys(finDeVie)
    .map((i: string) => (parseFloat(finDeVie[i]) ? parseFloat(finDeVie[i]) : 0))
    .reduce((acc, curr) => acc + curr, 0);

  const chartRef = useRef<any>(null);

  useEffect(() => {
    setActive(-1);
    const impacts = [
      'acd',
      'bvi',
      'etf',
      'htc',
      'htn',
      'ozd',
      'cch',
      'fwe',
      'swe',
      'tre',
      'pco',
      'pma',
      'ior',
      'fru',
      'mru',
      'ldu',
      'tre',
      // 'pef',
      'wtu',
    ];

    const newGlobal = { ...props.impacts_global };
    const newMatPrem: any = {};
    const newTransfo: any = {};
    const newTransport: any = {};
    const newUtil: any = {};
    const newFinDeVie: any = {};

    impacts.forEach((i) => {
      newGlobal[i] = props.impacts_global[i];
      newMatPrem[i] = Math.round(
        (parseFloat(props.impact_lifecycle.Matierespremieres.global[i]) /
          parseFloat(props.impacts_global[i])) *
          100,
      );

      newTransfo[i] = Math.round(
        ((parseFloat(props.impact_lifecycle.Filature.global[i]) +
          parseFloat(props.impact_lifecycle['Tissage&Tricotage'].global[i]) +
          parseFloat(props.impact_lifecycle.Ennoblissement.global[i]) +
          parseFloat(props.impact_lifecycle.Confection.global[i])) /
          props.impacts_global[i]) *
          100,
      );
      newTransport[i] = Math.round(
        (parseFloat(props.impact_transport[i]) /
          parseFloat(props.impacts_global[i])) *
          100,
      );
      newUtil[i] = Math.round(
        (parseFloat(props.impact_lifecycle.Utilisation.global[i]) /
          parseFloat(props.impacts_global[i])) *
          100,
      );
      newFinDeVie[i] = Math.round(
        (parseFloat(props.impact_lifecycle.Findevie.global[i]) /
          parseFloat(props.impacts_global[i])) *
          100,
      );
    });

    setGlobal(sortObject(newGlobal));
    setMatPrem(sortObject(newMatPrem));
    setTransfo(sortObject(newTransfo));
    setTransport(sortObject(newTransport));
    setUtil(sortObject(newUtil));
    setFinDeVie(sortObject(newFinDeVie));

    handleReset();

    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.update();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [props.impact_lifecycle, props.impacts_global, props.impact_transport]);

  return (
    <div className="my-12 border-blackOverlay dark:border-whiteOverlay border-2 rounded-2xl p-4 block">
      <div className="flex justify-center items-center mb-6 ">
        <p className="md:text-lg text-sm font-medium ">
          Répartition de l'impact dans le cycle de vie
        </p>
      </div>

      <div
        className="w-full mt-10 mb-10 h-50 md:h-32 block"
        style={{ minHeight: '200px' }}
      >
        <Chart
          type="bar"
          hidden={false}
          ref={chartRef}
          options={options}
          data={data}
          onClick={handleClick}
        />
      </div>
      <div>
        {active === -1 && (
          <div className="flex flex-col">
            <p className="md:text-xl text-base font-bold pb-3">
              Valeurs Globales
            </p>
            {data.datasets
              .map((dataset, index) => {
                const dataValue: any = dataset.data[0];
                const globalLineName = getGlobalName(index);
                const globalLineValue =
                  dataValue !== null
                    ? parseFloat(String(dataValue)).toFixed(2)
                    : '0.00';

                const percentage: any =
                  dataValue !== null
                    ? (
                        (parseFloat(String(dataValue)) / globalData) *
                        100
                      ).toFixed(0)
                    : 0.0;
                return {
                  index,
                  globalLineName,
                  globalLineValue,
                  percentage,
                  dataset,
                };
              })
              .sort(
                (a, b) => parseFloat(b.percentage) - parseFloat(a.percentage),
              )
              .map(
                ({
                  index,
                  globalLineName,
                  globalLineValue,
                  dataset,
                  percentage,
                }) => (
                  <div
                    key={index}
                    className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                      globalLineName
                        ? 'text-black dark:text-white'
                        : impact === dataset.label
                        ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                        : 'text-blackLight dark:text-whiteLight transform scale-95'
                    }`}
                    onClick={() => handleLineClick(dataset, index)}
                  >
                    <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                      <p className="text-xs">{globalLineValue + ' Pts'}</p>
                    </div>
                    <p className="md:text-sm text-xs w-[48%] md:w-full">
                      {getGlobalName(index)}
                    </p>
                    <div className="md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay">
                      <div
                        className={`h-1 rounded-full bg-black dark:bg-white w-[${Number(
                          percentage,
                        )}%]`}
                      ></div>
                    </div>
                  </div>
                ),
              )}
          </div>
        )}
        {active === 0 && (
          <div>
            <div className="flex justify-between items-center">
              <p className="md:text-xl text-base font-bold">
                Matières premières
              </p>
              <svg
                className="md:w-6 w-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => handleReset()}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L6.53035 18.5303C6.23745 18.8232 5.76258 18.8232 5.46969 18.5303C5.17679 18.2374 5.17679 17.7626 5.46968 17.4697L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              {Object.keys(matPrem)
                .map((i: string) => {
                  const isActive = impact === i;
                  const percentage: any = !Number.isNaN(matPrem[i])
                    ? (
                        (parseFloat(String(matPrem[i])) /
                          totalPercentageMatPremiere) *
                        100
                      ).toFixed(0)
                    : 0;

                  return {
                    matPrem,
                    isActive,
                    i,
                    percentage,
                  };
                })
                .sort((a, b) => b.percentage - a.percentage)
                .map(({ matPrem, isActive, i, percentage }) => (
                  <div
                    key={i}
                    className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                      impact === 'ecs'
                        ? 'text-black dark:text-white'
                        : impact === i
                        ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                        : 'text-blackLight dark:text-whiteLight transform scale-95'
                    }`}
                    onClick={() => handleImpactClick(i)}
                  >
                    <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                      <p className="text-xs">
                        {parseFloat(
                          props.impact_lifecycle.Matierespremieres.global[i],
                        ).toFixed(5) +
                          ' ' +
                          units[i]}
                      </p>
                    </div>
                    <p className="md:text-sm text-xs w-[48%] md:w-full">
                      {getImpactName(i)}
                    </p>
                    <div
                      className={`md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay ${
                        isActive
                          ? 'bg-blackOverlay dark:bg-whiteOverlay'
                          : 'bg-gray-300 dark:bg-gray-600 '
                      } ${
                        impact === 'ecs'
                          ? 'opacity-100'
                          : impact === i
                          ? 'opacity-100'
                          : 'opacity-50'
                      }`}
                    >
                      <div
                        className={`w-[${
                          totalPercentageMatPremiere === 0 ? 0 : percentage
                        }%] h-1 rounded-full bg-black dark:bg-white`}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        {active === 1 && (
          <div>
            <div className="flex justify-between items-center">
              <p className="md:text-xl text-base font-bold">Transformations</p>
              <svg
                className="md:w-6 w-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => handleReset()}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L6.53035 18.5303C6.23745 18.8232 5.76258 18.8232 5.46969 18.5303C5.17679 18.2374 5.17679 17.7626 5.46968 17.4697L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              {Object.keys(transfo)
                .map((i: string) => {
                  const isActive = impact === i;
                  const transfoLineValue: number =
                    parseFloat(props.impact_lifecycle.Filature.global[i]) +
                    parseFloat(
                      props.impact_lifecycle['Tissage&Tricotage'].global[i],
                    ) +
                    parseFloat(props.impact_lifecycle.Ennoblissement.global[i]);
                  const percentage: any = !Number.isNaN(transfo[i])
                    ? (
                        (parseFloat(String(transfo[i])) /
                          totalPercentageTransformation) *
                        100
                      ).toFixed(0)
                    : 0;

                  return {
                    transfo,
                    isActive,
                    transfoLineValue,
                    i,
                    percentage,
                  };
                })
                .sort((a, b) => b.percentage - a.percentage)
                .map(
                  ({ transfo, isActive, transfoLineValue, i, percentage }) => (
                    <div
                      key={i}
                      className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-2 px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                        impact === 'ecs'
                          ? 'text-black dark:text-white'
                          : impact === i
                          ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                          : 'text-blackLight dark:text-whiteLight transform scale-95'
                      }`}
                      onClick={() => handleImpactClick(i)}
                    >
                      <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                        <p className="text-xs">
                          {transfoLineValue + ' ' + units[i]}
                        </p>
                      </div>
                      <p className="md:text-sm text-xs w-[48%] md:w-full">
                        {getImpactName(i)}
                      </p>
                      <div
                        className={`md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay ${
                          isActive
                            ? 'bg-blackOverlay dark:bg-whiteOverlay'
                            : 'bg-gray-300 dark:bg-gray-600 '
                        } ${
                          impact === 'ecs'
                            ? 'opacity-100'
                            : impact === i
                            ? 'opacity-100'
                            : 'opacity-50'
                        }`}
                      >
                        <div
                          className={`w-[${
                            percentage === 'NaN' ? 0 : percentage
                          }%] h-1 rounded-full bg-black dark:bg-white`}
                        ></div>
                      </div>
                    </div>
                  ),
                )}
            </div>
          </div>
        )}
        {active === 2 && (
          <div>
            <div className="flex justify-between items-center">
              <p className="md:text-xl text-base font-bold">Transport</p>
              <svg
                className="md:w-6 w-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => handleReset()}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L6.53035 18.5303C6.23745 18.8232 5.76258 18.8232 5.46969 18.5303C5.17679 18.2374 5.17679 17.7626 5.46968 17.4697L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              {Object.keys(transport)
                .map((i: string) => {
                  const isActive = impact === i;
                  const percentage: any = !Number.isNaN(transport[i])
                    ? (
                        (parseFloat(String(transport[i])) /
                          totalPercentageTransport) *
                        100
                      ).toFixed(0)
                    : 0;

                  return {
                    transport,
                    isActive,
                    i,
                    percentage,
                  };
                })
                .sort((a, b) => b.percentage - a.percentage)
                .map(({ transport, isActive, i, percentage }) => (
                  <div
                    key={i}
                    className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-2 px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                      impact === 'ecs'
                        ? 'text-black dark:text-white'
                        : impact === i
                        ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                        : 'text-blackLight dark:text-whiteLight transform scale-95'
                    }`}
                    onClick={() => handleImpactClick(i)}
                  >
                    <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                      <p className="text-xs">
                        {parseFloat(props.impact_transport[i]).toFixed(6) +
                          ' ' +
                          units[i]}
                      </p>
                    </div>
                    <p className="md:text-sm text-xs md:w-full w-[48%]">
                      {getImpactName(i)}
                    </p>
                    <div
                      className={`md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay ${
                        isActive
                          ? 'bg-blackOverlay dark:bg-whiteOverlay'
                          : 'bg-gray-300 dark:bg-gray-600 '
                      } ${
                        impact === 'ecs'
                          ? 'opacity-100'
                          : impact === i
                          ? 'opacity-100'
                          : 'opacity-50'
                      }`}
                    >
                      <div
                        className={`w-[${
                          percentage === 'NaN' ? 0 : percentage
                        }%] h-1 rounded-full bg-black dark:bg-white`}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        {active === 3 && (
          <div>
            <div className="flex justify-between items-center">
              <p className="md:text-xl text-base font-bold">Utilisation</p>
              <svg
                className="md:w-6 w-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => handleReset()}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L6.53035 18.5303C6.23745 18.8232 5.76258 18.8232 5.46969 18.5303C5.17679 18.2374 5.17679 17.7626 5.46968 17.4697L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              {Object.keys(util)
                .map((i: string) => {
                  const isActive = impact === i;
                  const percentage: any = !Number.isNaN(util[i])
                    ? (
                        (parseFloat(String(util[i])) /
                          totalPercentageUtilisation) *
                        100
                      ).toFixed(0)
                    : 0;
                  return {
                    util,
                    isActive,
                    i,
                    percentage,
                  };
                })
                .sort((a, b) => b.percentage - a.percentage)
                .map(({ util, isActive, i, percentage }) => (
                  <div
                    key={i}
                    className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-2 px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                      impact === 'ecs'
                        ? 'text-black dark:text-white'
                        : impact === i
                        ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                        : 'text-blackLight dark:text-whiteLight transform scale-95'
                    }`}
                    onClick={() => handleImpactClick(i)}
                  >
                    <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                      <p className="text-xs">
                        {parseFloat(
                          props.impact_lifecycle.Utilisation.global[i],
                        ).toFixed(5) +
                          ' ' +
                          units[i]}
                      </p>
                    </div>
                    <p className="md:text-sm text-xs md:w-full w-[48%]">
                      {getImpactName(i)}
                    </p>
                    <div
                      className={`md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay ${
                        isActive
                          ? 'bg-blackOverlay dark:bg-whiteOverlay'
                          : 'bg-gray-300 dark:bg-gray-600 '
                      } ${
                        impact === 'ecs'
                          ? 'opacity-100'
                          : impact === i
                          ? 'opacity-100'
                          : 'opacity-50'
                      }`}
                    >
                      <div
                        className={`w-[${
                          percentage === 'NaN' ? 0 : percentage
                        }%] h-1 rounded-full bg-black dark:bg-white`}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        {active === 4 && (
          <div>
            <div className="flex justify-between items-center">
              <p className="md:text-xl text-base font-bold">Fin de vie</p>
              <svg
                className="md:w-6 w-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => handleReset()}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L6.53035 18.5303C6.23745 18.8232 5.76258 18.8232 5.46969 18.5303C5.17679 18.2374 5.17679 17.7626 5.46968 17.4697L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              {Object.keys(finDeVie)
                .map((i: string) => {
                  const isActive = impact === i;

                  const percentage: any = !Number.isNaN(finDeVie[i])
                    ? (
                        (parseFloat(String(finDeVie[i])) /
                          totalPercentageFinDeVie) *
                        100
                      ).toFixed(0)
                    : 0;
                  return {
                    finDeVie,
                    isActive,
                    i,
                    percentage,
                  };
                })
                .sort((a, b) => b.percentage - a.percentage)
                .map(({ finDeVie, isActive, i, percentage }) => (
                  <div
                    key={i}
                    className={`group relative flex justify-between items-center cursor-pointer p-2 md:px-2 px-0 transition-all hover:bg-blackOverlay hover:dark:bg-whiteOverlay ${
                      impact === 'ecs'
                        ? 'text-black dark:text-white'
                        : impact === i
                        ? 'bg-blackOverlay dark:bg-whiteOverlay font-medium transform scale-102'
                        : 'text-blackLight dark:text-whiteLight transform scale-95'
                    }`}
                    onClick={() => handleImpactClick(i)}
                  >
                    <div className="hidden group-hover:block absolute bottom-8 left-2 bg-secondary p-2 z-20 rounded-2xl">
                      <p className="text-xs">
                        {parseFloat(
                          props.impact_lifecycle.Findevie.global[i],
                        ).toFixed(5) +
                          ' ' +
                          units[i]}
                      </p>
                    </div>
                    <p className="md:text-sm text-xs md:w-full w-[48%]">
                      {getImpactName(i)}
                    </p>
                    <div
                      className={`md:w-56 w-[48%] h-1 rounded-full bg-blackOverlay dark:bg-whiteOverlay ${
                        isActive
                          ? 'bg-blackOverlay dark:bg-whiteOverlay'
                          : 'bg-gray-300 dark:bg-gray-600 '
                      } ${
                        impact === 'ecs'
                          ? 'opacity-100'
                          : impact === i
                          ? 'opacity-100'
                          : 'opacity-50'
                      }`}
                    >
                      <div
                        className={`w-[${
                          percentage === 'NaN' ? 0 : percentage
                        }%] h-1 rounded-full bg-black dark:bg-white`}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
